import { useEffect, useState, useCallback } from "react";
import { getAdminUsers } from "../../services/ingroove/adminService";
import { formatDate } from "../../utils/formatters";
import Avatar from "../../components/common/Avatar";
import PageHeader from "../../components/common/PageHeader";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalRecords: 0, totalPages: 0 });
  const navigate = useNavigate();

  const fetchUsers = useCallback(async (searchVal, pageVal) => {
    setLoading(true);
    try {
      const res = await getAdminUsers({
        search: searchVal,
        pageNumber: pageVal,
        pageSize: 20,
      });
      setUsers(res.data);
      setMeta({ totalRecords: res.totalRecords, totalPages: res.totalPages });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchUsers(search, 1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchUsers(search, page);
  }, [page]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        subtitle={`${meta.totalRecords.toLocaleString()} registered users`}
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
        />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-lg text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
        />
      </div>

      {/* Table */}
      <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-elevated text-text-muted text-xs uppercase">
            <tr>
              <th className="px-5 py-3 text-left">User</th>
              <th className="px-5 py-3 text-left">Email</th>
              <th className="px-5 py-3 text-left">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-5 py-3">
                    <div className="h-4 bg-bg-elevated rounded w-32" />
                  </td>
                  <td className="px-5 py-3">
                    <div className="h-4 bg-bg-elevated rounded w-48" />
                  </td>
                  <td className="px-5 py-3">
                    <div className="h-4 bg-bg-elevated rounded w-24" />
                  </td>
                </tr>
              ))
            ) : users.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-5 py-12 text-center text-text-muted text-xs"
                >
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="hover:bg-bg-elevated transition cursor-pointer"
                  onClick={() => navigate(`/ingroove/users/${user.id}`)}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar
                        name={user.name}
                        surname={user.surname}
                        image={user.image}
                        size="sm"
                      />
                      <span className="text-text-primary font-medium">
                        {user.name} {user.surname}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-text-secondary">
                    {user.email}
                  </td>
                  <td className="px-5 py-3 text-text-muted">
                    {formatDate(user.joinedAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            Page {page} of {meta.totalPages} —{" "}
            {meta.totalRecords.toLocaleString()} users
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
